import React, {
  FunctionComponent, ReactChild, useState, useEffect,
} from 'react'
import {
  Col,
  Row,
  Form,
  Carousel,
  CarouselItem,
  CarouselIndicators,
  Button,
} from 'reactstrap'
import FormField from 'components/common/FormField'
import { StyledButton } from 'components/common/PrimaryButton'
import { useForm } from 'react-hook-form'
import logoDark from 'assets/img/logos/logo-word-dark.png'
import styled from 'styled-components'
import colors from 'constants/colors'
import { useSession } from 'components/auth/Session'
import { Redirect, Link } from 'react-router-dom'
import { Cognito } from 'components/auth/Cognito'
import { ISignUpResult, CognitoUser } from 'amazon-cognito-identity-js'
import { gql, useMutation } from '@apollo/client'
import { useLoading, Loading } from 'components/common/Loading'
import { GoogleButton } from 'components/auth/GoogleButton'
import { GdprAcceptance } from './GdprAcceptance'

const CarouselIndicatorsStyled = styled(CarouselIndicators)`
  position: relative;
  bottom: initial;
  margin-bottom: 0;
  margin-top: 15px;

  li {
    background-color: #EBB02F;
    border-radius: 30px;
    height: 30px;
    border-top: initial;
    border-bottom: initial;
    text-indent: initial;
    color: white;
    font-family: Poppins;
    font-weight: 600;
    font-size: 16px;
  }

  li:before {
    content: "1";
    position: relative;
    top: 3px;
  }
  li + li:before {
    content: "2";
    position: relative;
    top: 3px;
  }

  li + li + li:before {
    content: "3";
    position: relative;
    top: 3px;
  }
`

const CarouselStyled = styled(Carousel)`
  max-width: 500px;
  margin: 0 auto;
`

const CarouselInner = styled.div`
  background-color: white;
  padding: 30px 15px;
`

const NextButton = styled(StyledButton)`
  background-color: #53C8A4;
  border: 0;
  &:hover {
    background-color: ${colors.green};
    border-color: ${colors.green};
  }
  &:focus {
    background-color: ${colors.green};
    border-color: ${colors.green};
  }
  &:active {
    background-color: ${colors.green} !important;
    border-color: ${colors.green} !important;
  }
`

const PrevButton = styled(Button)`
  background-color: white;
  border: 0;
  color: ${colors.black};
  font-size: 12px;
  font-weight: 500;
  font-family: Poppins;

  &:hover {
    background-color: ${colors.lightGreen};
    border-color: ${colors.lightGreen};
    color: ${colors.green};
  }
  &:focus {
    background-color: ${colors.lightGreen};
    border-color: ${colors.lightGreen};
    color: ${colors.green};
  }
  &:active {
    background-color: ${colors.lightGreen} !important;
    border-color: ${colors.lightGreen} !important;
    color: ${colors.green};
  }
`

const CarouselTitle = styled.h1`
  font-family: Poppins;
  font-weight: 600;
  font-size: 24px;
  color: #242735;
`

const CarouselSubtitle = styled.p`
  font-family: Poppins;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.grey};
  margin-bottom: 20px;
`

const ErrorMessage = styled.div`
  font-family: Poppins;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.error};
`

const signupMutation = gql`
  mutation Signup($jwt: String!, $input: SignupInput!) {
    signup(jwt: $jwt, input: $input) {
      space {
        id
      }
    }
  }
`

type SlideLayoutProps = {
  hideBack?: boolean,
  nextTitle: string,
  onPrevClick?: () => void,
  onNextClick?: () => void,
  children: ReactChild,
}

const SlideLayout = (props: SlideLayoutProps) => {
  const backNode = props.hideBack ? null : (
    <PrevButton
      outline
      onClick={props.onPrevClick}
      className="mr-4 w-100"
    >
      Back
    </PrevButton>
  )

  return (
    <>
      <Row>
        <Col xs="12">
          {props.children}
        </Col>
      </Row>

      <Row className="mt-2">
        <Col xs="12">
          <NextButton
            className="pl-115 w-100"
            onClick={props.onNextClick}
          >
            {props.nextTitle}
          </NextButton>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col xs="12">
          {backNode}
        </Col>
      </Row>
    </>
  )
}

interface UserInfo {
  email: string
  password: string
  name?: string
}

interface SignupFormData extends UserInfo {
  password2: string
  orgName: string
  locationName: string
  spaceName: string
  capacity: string
  gdpr: boolean
}

type SignupFormFields = keyof SignupFormData

const cognitoSignIn = async ({ email, password }: UserInfo) => {
  let signInErr: Error | null = null
  let user: CognitoUser | null = null
  try {
    user = await Cognito.signIn({
      username: email,
      password,
    })
  } catch (err) {
    signInErr = err
  }
  return { signInErr, user }
}

const cognitoSignUp = async ({ name, email, password }: UserInfo) => {
  let signUpErr: Error | null = null
  let response: ISignUpResult | null = null
  try {
    response = await Cognito.signUp({
      username: email,
      password,
      attributes: { name },
    })
  } catch (err) {
    signUpErr = err
  }
  return { signUpErr, response }
}

export const SignupForm: FunctionComponent = () => {
  const [submissionErrors, setSubmissionErrors] = useState<Error[] | null>(null)
  const {
    handleSubmit, register, errors, trigger, getValues,
  } = useForm<SignupFormData>()
  const [activeIndex, setActiveIndex] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [signup] = useMutation(signupMutation)
  const session = useSession()
  const { withLoading } = useLoading()
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | false | undefined>(undefined)

  useEffect(() => {
    if (session.user) return

    Cognito.currentAuthenticatedUser().then((u) => {
      setCognitoUser(u)
    }).catch(() => {
      setCognitoUser(false)
    })
  }, [setCognitoUser, session.user])

  const setCognitoError = (errs: Error[] | null) => {
    setSubmissionErrors(errs)
    setActiveIndex(0)
  }

  if (session.user) {
    return <Redirect to={session.initialUrl} />
  }

  if (cognitoUser === undefined) {
    return <Loading />
  }

  const onSubmit = withLoading(async (data: SignupFormData) => {
    let user: CognitoUser | null

    if (cognitoUser) {
      user = cognitoUser
    } else {
      const { signUpErr } = await cognitoSignUp(data)
      if (signUpErr) {
        setCognitoError([signUpErr])
        return
      }

      const signInResp = await cognitoSignIn(data)
      if (signInResp.signInErr) {
        setCognitoError([signInResp.signInErr])
        return
      }

      if (!signInResp.user) {
        setCognitoError([new Error('User not found')])
        return
      }

      user = signInResp.user
      setCognitoUser(user)
    }

    const token = user.getSignInUserSession()?.getIdToken()
    if (!token) {
      setCognitoError([new Error('User not authorized')])
      return
    }

    const locationSlug = data.locationName.toLowerCase().replace(/[^0-9a-z]/g, '-')

    const signupResp = await signup({
      variables: {
        jwt: token.getJwtToken(),
        input: {
          name: data.orgName,
          location: {
            text: data.locationName,
            slug: locationSlug,
          },
          space: {
            capacity: data.capacity ? parseInt(data.capacity, 10) : null,
            name: data.spaceName,
            location: {
              slug: locationSlug,
            },
          },
        },
      },
    })

    if (signupResp.errors) {
      setSubmissionErrors(signupResp.errors as unknown as Error[])
      return
    }

    const spaceId = signupResp.data?.signup?.space?.id
    if (spaceId) {
      session.setInitialUrl(`/spaces/${spaceId}`)
    }

    await session.login(user)
  })

  const next = () => {
    if (animating) return
    setActiveIndex(activeIndex + 1)
  }

  const previous = () => {
    if (animating) return
    if (activeIndex === 0) return
    setActiveIndex(activeIndex - 1)
  }

  const withValidation = (fields: SignupFormFields[], nextFunc: () => void) => {
    return () => {
      if (!fields) {
        nextFunc()
      }
      trigger(fields).then((isValid) => {
        if (isValid) {
          nextFunc()
        }
      })
    }
  }

  const items = [
    {
      key: '1',
      title: 'Welcome to BirdLevel',
      subtitle: "Let's start by creating your account in 3 easy steps",
      node: (cognitoUser ? (
        <SlideLayout
          onNextClick={withValidation(['gdpr'], next)}
          onPrevClick={previous}
          nextTitle="Get Started"
          hideBack
        >
          <>
            <p>
              You are signing up as: <br />
              <span className="h6">{ cognitoUser.getSignInUserSession()?.getIdToken().decodePayload()['email'] }</span>
            </p>

            <p className="small"><Link to="/auth/logout">Use a different account</Link></p>

            <GdprAcceptance
              name="gdpr"
              feedback={errors.gdpr?.message}
              innerRef={
                register({
                  required: 'Accepting the privacy policy is required to use the app.',
                })
              }
              invalid={Boolean(errors.gdpr)}
            />
          </>
        </SlideLayout>
      ) : (
        <SlideLayout
          onNextClick={withValidation(['name', 'email', 'password', 'password2', 'gdpr'], next)}
          onPrevClick={previous}
          nextTitle="Get Started"
          hideBack
        >
          <>
            <GoogleButton title="Sign up with Google" />

            <hr className="mt-4 mb-4" />

            <p className="small"> or sign up with your email </p>

            <FormField
              placeholder="Name"
              name="name"
              innerRef={register({
                required: 'Name is required',
              })}
              feedback={errors.name?.message}
              invalid={Boolean(errors.name)}
            />
            <FormField
              placeholder="Email"
              name="email"
              type="text"
              feedback={errors.email?.message}
              innerRef={register({
                min: 0,
                required: 'Email is required',
              })}
              invalid={Boolean(errors.email)}
            />
            <FormField
              placeholder="Password"
              name="password"
              type="password"
              feedback={errors.password?.message}
              innerRef={register({
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
                required: 'Password is required',
              })}
              invalid={Boolean(errors.password)}
            />
            <FormField
              placeholder="Confirm Password"
              name="password2"
              type="password"
              feedback={errors.password2?.message}
              innerRef={register({
                validate: (val) => (val !== getValues('password') ? 'Passwords do not match' : true),
                required: 'Please retype your password',
              })}
              invalid={Boolean(errors.password2)}
            />
            <GdprAcceptance
              name="gdpr"
              feedback={errors.gdpr?.message}
              innerRef={
                register({
                  required: 'Accepting the privacy policy is required to use the app.',
                })
              }
              invalid={Boolean(errors.gdpr)}
            />
          </>
        </SlideLayout>
      )),
    },
    {
      key: '2',
      title: "What's Your Organizations Name?",
      subtitle: 'This will allow you to invite team members later on',
      node: (
        <SlideLayout
          onNextClick={withValidation(['orgName'], next)}
          onPrevClick={previous}
          nextTitle="Next"
        >
          <FormField
            placeholder="Organization name"
            name="orgName"
            innerRef={register({
              required: 'Organization is required',
            })}
            feedback={errors.orgName?.message}
            invalid={Boolean(errors.orgName)}
          />
        </SlideLayout>
      ),
    },
    {
      key: '3',
      title: 'Finally, add your first Space',
      subtitle: (
        <span>
          A <b><i>location</i></b> could be the city of your office or a building on campus.<br />
          A <b><i>space</i></b> is anywhere at that location you want to track check-ins.<br />
          (You can change these later.)
        </span>
      ),
      node: (
        <SlideLayout
          onNextClick={withValidation(['locationName', 'spaceName', 'capacity'], handleSubmit(onSubmit))}
          onPrevClick={previous}
          nextTitle="Signup"
        >
          <>
            <FormField
              placeholder="Location (i.e. 'NY Office' or 'Graduate Center')"
              name="locationName"
              innerRef={register({
                required: 'Location name is required',
              })}
              feedback={errors.locationName?.message}
              invalid={Boolean(errors.locationName)}
            />
            <FormField
              placeholder="Space (i.e. 'Entrance' or 'Room 101')"
              name="spaceName"
              innerRef={register({
                required: 'Space name is required',
              })}
              feedback={errors.spaceName?.message}
              invalid={Boolean(errors.spaceName)}
            />
            <FormField
              placeholder="Normal Capacity (optional)"
              name="capacity"
              innerRef={register({
                required: false,
              })}
              feedback={errors.capacity?.message}
              invalid={Boolean(errors.capacity)}
            />
          </>
        </SlideLayout>
      ),
    },
  ]

  const slides = items.map((item) => {
    return (
      <CarouselItem
        onExiting={() => setAnimating(true)}
        onExited={() => setAnimating(false)}
        key={item.key}
      >
        <CarouselInner>
          <Row className="">
            <Col>
              <CarouselTitle>{item.title}</CarouselTitle>
              <CarouselSubtitle>{item.subtitle}</CarouselSubtitle>
            </Col>
          </Row>
          {item.node}
        </CarouselInner>
      </CarouselItem>
    )
  })

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <img alt="Bird Level logo" src={logoDark} className="section mb-3" style={{ width: '100px' }} />

      {submissionErrors?.map((err) => <ErrorMessage>{err.message}</ErrorMessage>)}

      <CarouselStyled
        activeIndex={activeIndex}
        next={next}
        previous={previous}
        interval={false}
        keyboard={false}
      >
        <CarouselIndicatorsStyled items={items} activeIndex={activeIndex} />
        {slides}
      </CarouselStyled>
    </Form>
  )
}

export default SignupForm
